import React, { useState, useMemo } from 'react';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Calendar, dayjsLocalizer } from 'react-big-calendar';
import dayjs from 'dayjs';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Chip,
  Divider,
  Alert,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Card,
  CardContent,
  IconButton
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  AccessTime as TimeIcon,
  CalendarToday as CalendarIcon,
  Close as CloseIcon,
  Twitter as TwitterIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  AutoAwesome as AutoIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Folder as FolderIcon,
  ViewList as ListIcon,
  CalendarMonth as CalendarMonthIcon,
  SmartToy as SmartIcon,
  Timeline as TimelineIcon,
  DateRange as DateRangeIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const localizer = dayjsLocalizer(dayjs);

const getPlatformIcon = (platform) => {
  switch (platform.toLowerCase()) {
    case 'twitter':
      return <TwitterIcon />;
    case 'facebook':
      return <FacebookIcon />;
    case 'instagram':
      return <InstagramIcon />;
    case 'linkedin':
      return <LinkedInIcon />;
    default:
      return null;
  }
};

const getPlatformColor = (platform) => {
  switch (platform.toLowerCase()) {
    case 'twitter':
      return '#1DA1F2';
    case 'facebook':
      return '#4267B2';
    case 'instagram':
      return '#E4405F';
    case 'linkedin':
      return '#0077B5';
    default:
      return '#757575';
  }
};

// Enhanced styled components for better design
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(2),
    minHeight: '600px',
    maxHeight: '90vh',
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.main}05 100%)`,
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(3),
}));

const SchedulingSection = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1.5),
  '& .MuiCardContent-root': {
    padding: theme.spacing(3),
    '&:last-child': {
      paddingBottom: theme.spacing(3),
    }
  }
}));

const OptionCard = styled(Paper)(({ theme, selected }) => ({
  padding: theme.spacing(2),
  border: `2px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
  borderRadius: theme.spacing(1),
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.main + '08',
  },
  ...(selected && {
    backgroundColor: theme.palette.primary.main + '12',
  })
}));

const PreviewSection = styled(Card)(({ theme }) => ({
  height: 'fit-content',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1.5),
}));

const PostPreviewItem = styled(ListItem)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(1),
  backgroundColor: theme.palette.background.default,
  '&:hover': {
    backgroundColor: theme.palette.action.hover
  }
}));

const SequenceAccordion = styled(Accordion)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: theme.spacing(1, 0),
  }
}));

const StyledCalendar = styled(Calendar)(({ theme }) => ({
  height: 320,
  '.rbc-calendar': {
    fontSize: theme.typography.body2.fontSize,
  },
  '.rbc-event': {
    backgroundColor: theme.palette.primary.main,
    fontSize: theme.typography.caption.fontSize,
    padding: theme.spacing(0.25, 0.5),
    borderRadius: theme.spacing(0.5),
  },
  '.rbc-event.twitter': {
    backgroundColor: '#1DA1F2',
  },
  '.rbc-event.facebook': {
    backgroundColor: '#4267B2',
  },
  '.rbc-event.instagram': {
    backgroundColor: '#E4405F',
  },
  '.rbc-event.linkedin': {
    backgroundColor: '#0077B5',
  },
  '.rbc-today': {
    backgroundColor: theme.palette.action.hover,
  },
  '.rbc-current-time-indicator': {
    backgroundColor: theme.palette.error.main,
  }
}));

const ScheduleDialog = ({ 
  open, 
  onClose, 
  onConfirm, 
  posts = [],
  sequences = [] 
}) => {
  const [scheduleType, setScheduleType] = useState('smart');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [customTime, setCustomTime] = useState('09:00');
  const [intervals, setIntervals] = useState('4'); // hours between posts
  const [expandedSequence, setExpandedSequence] = useState(false);
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'
  
  // Check if any posts have been manually scheduled
  const hasManualSchedules = useMemo(() => {
    return posts.some(post => {
      const sequence = sequences.find(s => s.id === post.bucketId);
      if (!sequence) return false;
      return post.scheduledDate && post.isManuallyScheduled;
    });
  }, [posts, sequences]);

  // Initialize dates with reasonable defaults
  React.useEffect(() => {
    if (open) {
      // Always reset to current date when dialog opens
      const today = new Date();
      const start = new Date(today);
      start.setDate(1); // Start of current month
      
      const end = new Date(today.getFullYear(), today.getMonth() + 1, 0); // End of current month
      
      setStartDate(start.toISOString().split('T')[0]);
      setEndDate(end.toISOString().split('T')[0]);
    }
  }, [open]);

  // Debounced navigation handler to prevent multiple rapid calls
  const handleCalendarNavigation = React.useCallback(
    React.useMemo(
      () => {
        let timeoutId;
        return (date) => {
          clearTimeout(timeoutId);
          timeoutId = setTimeout(() => {
            const newStartDate = new Date(date.getFullYear(), date.getMonth(), 1);
            const newStartDateString = newStartDate.toISOString().split('T')[0];
            
            // Only update if the date has actually changed
            if (newStartDateString !== startDate) {
              setStartDate(newStartDateString);
            }
          }, 100); // 100ms debounce
        };
      },
      [startDate]
    ),
    [startDate]
  );

  const handleConfirm = () => {
    const scheduleData = {
      type: scheduleType,
      startDate,
      endDate,
      customTime,
      intervals: parseInt(intervals),
      posts: posts.map(post => post.id)
    };
    
    onConfirm(scheduleData);
  };

  const getSchedulePreview = () => {
    if (!startDate || posts.length === 0) return [];
    
    const start = new Date(startDate + 'T' + customTime);
    const intervalMs = parseInt(intervals) * 60 * 60 * 1000; // Convert hours to milliseconds
    
    return posts.map((post, index) => {
      const scheduledTime = new Date(start.getTime() + (index * intervalMs));
      return {
        ...post,
        scheduledTime
      };
    });
  };

  const schedulePreview = getSchedulePreview();

  const getOptimalTimes = () => {
    return [
      { platform: 'twitter', time: '9:00 AM, 1:00 PM, 5:00 PM' },
      { platform: 'facebook', time: '9:00 AM, 1:00 PM, 3:00 PM' },
      { platform: 'instagram', time: '11:00 AM, 2:00 PM, 5:00 PM' },
      { platform: 'linkedin', time: '8:00 AM, 12:00 PM, 6:00 PM' },
    ];
  };

  const getPlatformBreakdown = () => {
    const breakdown = posts.reduce((acc, post) => {
      acc[post.platform] = (acc[post.platform] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(breakdown);
  };
  
  // Convert posts to calendar events
  const calendarEvents = useMemo(() => {
    if (!posts || posts.length === 0) {
      return [];
    }

    const events = posts.map((post, index) => {
      let eventDate = post.scheduledDate;
      let eventTime = post.scheduledTime || '09:00';
      
      // If no scheduled date, calculate based on schedule type and start date
      if (!eventDate) {
        if (startDate) {
          const start = new Date(startDate + 'T' + eventTime);
          const intervalMs = parseInt(intervals) * 60 * 60 * 1000;
          eventDate = new Date(start.getTime() + (index * intervalMs));
        } else {
          // Fallback - use current date and spread posts
          eventDate = new Date();
          eventDate.setDate(eventDate.getDate() + index);
          eventDate.setHours(9 + index, 0, 0, 0); // 9 AM, 10 AM, 11 AM, etc.
        }
      } else {
        // Parse existing scheduled date
        if (typeof eventDate === 'string') {
          eventDate = new Date(eventDate + 'T' + eventTime);
        }
      }
      
      // Validate final date
      if (!eventDate || isNaN(eventDate.getTime())) {
        eventDate = new Date(); // Fallback to today
        eventDate.setHours(9 + index, 0, 0, 0);
      }
      
      const event = {
        id: post.id,
        title: (post.content || post.post_text || 'Untitled Post').substring(0, 30) + 
               ((post.content || post.post_text || '').length > 30 ? '...' : ''),
        start: eventDate,
        end: new Date(eventDate.getTime() + 30 * 60 * 1000), // 30 min duration
        resource: post,
        className: (post.platform || 'default').toLowerCase()
      };
      
      return event;
    });
    
    return events;
  }, [posts, startDate, intervals]);

  const scheduleOptions = [
    {
      id: 'smart',
      icon: <SmartIcon />,
      title: 'Smart Scheduling',
      description: 'AI optimizes posting times for maximum engagement based on your ICP',
      recommended: true
    },
    ...(hasManualSchedules ? [{
      id: 'hybrid',
      icon: <TimelineIcon />,
      title: 'Hybrid Scheduling',
      description: 'Keep manually scheduled posts, AI optimizes the rest'
    }] : []),
    {
      id: 'sequence',
      icon: <FolderIcon />,
      title: 'Sequence-Based Distribution',
      description: 'Respect content bucket timing (Week 1-2, Week 2-3, etc.)'
    },
    {
      id: 'even',
      icon: <DateRangeIcon />,
      title: 'Even Distribution',
      description: 'Posts spread evenly across the date range'
    },
    {
      id: 'custom',
      icon: <SettingsIcon />,
      title: 'Custom Schedule',
      description: 'Set specific intervals and times'
    }
  ];

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ScheduleIcon color="primary" sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h5" fontWeight="600" sx={{ mb: 0.5 }}>
              Schedule Approved Posts
            </Typography>
              <Typography variant="body2" color="text.secondary">
              Schedule {posts.length} approved post{posts.length !== 1 ? 's' : ''} across your campaign sequences
            </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {getPlatformBreakdown().map(([platform, count]) => (
              <Chip 
                key={platform}
                size="small" 
                icon={getPlatformIcon(platform)}
                label={count}
                sx={{ 
                  backgroundColor: getPlatformColor(platform) + '15',
                  color: getPlatformColor(platform),
                  fontWeight: 'bold'
                }}
              />
            ))}
          </Box>
          
          <IconButton 
            onClick={onClose} 
            sx={{ 
              ml: 2,
              color: 'text.secondary',
              '&:hover': { backgroundColor: 'action.hover' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </StyledDialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 4 }}>
          <Grid container spacing={4}>
            {/* Left Column - Scheduling Options */}
            <Grid item xs={12} lg={6}>
              <SchedulingSection>
                <CardContent>
                  <Typography variant="h6" fontWeight="600" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SettingsIcon color="primary" />
              Scheduling Options
            </Typography>
            
                  <Stack spacing={2}>
                    {scheduleOptions.map((option) => (
                      <OptionCard 
                        key={option.id}
                        selected={scheduleType === option.id}
                        onClick={() => setScheduleType(option.id)}
              >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                          <Box sx={{ color: 'primary.main', mt: 0.5 }}>
                            {option.icon}
                    </Box>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="subtitle2" fontWeight="600">
                                {option.title}
                        </Typography>
                              {option.recommended && (
                                <Chip 
                                  label="Recommended" 
                                  size="small" 
                                  color="success" 
                                  sx={{ fontSize: '0.7rem', height: 20 }}
                                />
                              )}
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {option.description}
                      </Typography>
                    </Box>
                    </Box>
                      </OptionCard>
                    ))}
                  </Stack>
                </CardContent>
              </SchedulingSection>

              {/* Date Range Section */}
              <SchedulingSection>
                <CardContent>
                  <Typography variant="h6" fontWeight="600" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon color="primary" />
                    Campaign Timeline
                      </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
              <TextField
                        label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                        size="medium"
              />
                    </Grid>
                    <Grid item xs={6}>
              <TextField
                        label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                        size="medium"
              />
                    </Grid>
                  </Grid>

            {scheduleType === 'custom' && (
                    <Box sx={{ mt: 3 }}>
                      <Divider sx={{ mb: 3 }} />
                      <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 2 }}>
                        Custom Settings
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                <TextField
                  label="Start Time"
                  type="time"
                  value={customTime}
                  onChange={(e) => setCustomTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                        </Grid>
                        <Grid item xs={6}>
                <TextField
                  label="Hours Between Posts"
                  type="number"
                  value={intervals}
                  onChange={(e) => setIntervals(e.target.value)}
                  inputProps={{ min: 1, max: 72 }}
                  fullWidth
                />
                        </Grid>
                      </Grid>
              </Box>
            )}
                </CardContent>
              </SchedulingSection>

              {/* Smart Scheduling Info */}
            {scheduleType === 'smart' && (
                <Alert 
                  severity="info" 
                  sx={{ 
                    borderRadius: 1.5,
                    '& .MuiAlert-message': { width: '100%' }
                  }}
                >
                  <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1 }}>
                    Optimal posting times based on your ICP:
                </Typography>
                  <Stack spacing={0.5}>
                {getOptimalTimes().map((time) => (
                      <Box key={time.platform} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getPlatformIcon(time.platform)}
                        <Typography variant="body2">
                    <strong>{time.platform}:</strong> {time.time}
                  </Typography>
                      </Box>
                ))}
                  </Stack>
              </Alert>
            )}

            {scheduleType === 'sequence' && (
                <Alert severity="info" sx={{ borderRadius: 1.5 }}>
                  <Typography variant="body2">
                  Posts will be scheduled according to their content bucket timing, ensuring your campaign story unfolds naturally over the specified weeks.
                </Typography>
              </Alert>
            )}
          </Grid>

            {/* Right Column - Preview */}
            <Grid item xs={12} lg={6}>
              <PreviewSection>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6" fontWeight="600" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarMonthIcon color="primary" />
                Schedule Preview
              </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip 
                  icon={<CheckCircleIcon />} 
                  label={`${posts.length} Approved`} 
                  color="success" 
                  size="small"
                        sx={{ fontWeight: 'bold' }}
                />
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(e, newMode) => newMode && setViewMode(newMode)}
                  size="small"
                >
                  <ToggleButton value="calendar">
                    <CalendarMonthIcon fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="list">
                    <ListIcon fontSize="small" />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>
            
                  <Box sx={{ minHeight: 350 }}>
              {viewMode === 'calendar' ? (
                      <Paper variant="outlined" sx={{ p: 1, borderRadius: 1 }}>
                  <StyledCalendar
                    localizer={localizer}
                    events={calendarEvents}
                    startAccessor="start"
                    endAccessor="end"
                    defaultView="month"
                    views={['month', 'week']}
                    date={startDate ? new Date(startDate) : new Date()}
                    onNavigate={handleCalendarNavigation}
                    onSelectEvent={(event) => {
                      // Handle event selection if needed
                    }}
                    eventPropGetter={(event) => {
                      return {
                        className: event.className,
                        style: {
                          fontSize: '10px',
                          padding: '2px 4px',
                          backgroundColor: getPlatformColor(event.resource?.platform) + '40',
                          borderColor: getPlatformColor(event.resource?.platform),
                          color: getPlatformColor(event.resource?.platform)
                        }
                      };
                    }}
                    components={{
                      event: ({ event }) => (
                              <Box sx={{ fontSize: '10px', fontWeight: 'bold', lineHeight: 1.2 }}>
                            {event.title}
                            <br />
                            <Box component="span" sx={{ fontSize: '8px', opacity: 0.8 }}>
                              {event.resource?.platform}
                            </Box>
                        </Box>
                      )
                    }}
                  />
                </Paper>
                    ) : (
                      <Box sx={{ maxHeight: 320, overflow: 'auto' }}>
                        <List dense>
                          {schedulePreview.map((post, index) => (
                            <PostPreviewItem key={post.id} sx={{ mb: 1 }}>
                              <ListItemIcon sx={{ minWidth: 40 }}>
                                <Avatar
                                  sx={{
                                    width: 32, 
                                    height: 32, 
                                    backgroundColor: getPlatformColor(post.platform) + '20',
                                    color: getPlatformColor(post.platform)
                                  }}
                                >
                                  {getPlatformIcon(post.platform)}
                                </Avatar>
                              </ListItemIcon>
                              
                              <ListItemText
                                primary={
                                  <Typography variant="body2" sx={{ 
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    fontWeight: 500
                          }}>
                            {post.content}
                          </Typography>
                        }
                        secondary={
                          post.scheduledTime ? 
                            `${post.scheduledTime.toLocaleDateString()} at ${post.scheduledTime.toLocaleTimeString([], { 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}` : 
                            null
                        }
                      />
                    </PostPreviewItem>
                  ))}
                </List>
                      </Box>
              )}
            </Box>
                </CardContent>
              </PreviewSection>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions 
        sx={{ 
          p: 3, 
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.default'
        }}
      >
        <Button
          onClick={onClose}
          startIcon={<CloseIcon />}
          size="large"
          sx={{ px: 3 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          startIcon={<ScheduleIcon />}
          disabled={!startDate || posts.length === 0}
          size="large"
          sx={{ 
            px: 4,
            fontWeight: 'bold',
            boxShadow: 2,
            '&:hover': { boxShadow: 4 }
          }}
        >
          Schedule {posts.length} Approved Post{posts.length !== 1 ? 's' : ''}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default ScheduleDialog; 